import { type ClassValue, clsx } from "clsx";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { twMerge } from "tailwind-merge";

// A helper function for retrying an async operation
export const fetchWithRetry = async <T>(
	apiCall: () => Promise<T | null>,
	options?: {
		retries?: number;
		initialDelay?: number;
		isRetryableError?: (error: unknown) => boolean;
	},
): Promise<T | null> => {
	const {
		retries = 5,
		initialDelay = 500,
		isRetryableError = () => false, // Default: no errors are retryable
	} = options || {};

	let delay = initialDelay;
	for (let i = 0; i < retries; i++) {
		try {
			const result = await apiCall();
			// If we get a result, we're done.
			if (result) {
				return result;
			}
			// If the result is null, it's a "soft failure" (user not found yet).
			// We'll fall through to the delay and retry.
		} catch (error) {
			// Now we check if the caught error is one we should retry on.
			if (isRetryableError(error)) {
				// This is a known, retryable error (like a 404).
				// We'll log it and fall through to the delay and retry.
			} else {
				// This is a fatal, non-retryable error. Stop everything.
				console.error("API call failed with a non-retryable error:", error);
				return null;
			}
		}

		const retriesLeft = retries - i - 1;
		if (retriesLeft > 0) {
			console.log(
				`User not found or API returned a retryable error. Retrying in ${delay}ms... (${retriesLeft} retries left)`,
			);
			// Wait for the delay period
			await new Promise((resolve) => setTimeout(resolve, delay));
			// Double the delay for the next attempt (exponential backoff)
			delay *= 2;
		}
	}

	// If we exit the loop, all retries have failed.
	console.error("Failed to fetch data after multiple retries.");
	return null;
};

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function createdAtFormatter(
	createdAt: string | number | Date | { seconds: number; nanoseconds: number },
): string {
	if (!createdAt) {
		return "";
	}

	// Handle Timestamp type
	if (typeof createdAt === "object" && "seconds" in createdAt) {
		return formatDistanceToNow(new Date(createdAt.seconds * 1000), {
			addSuffix: true,
			locale: fr,
		});
	}

	// Handle other types
	const date = new Date(createdAt);
	if (isNaN(date.getTime())) {
		return "";
	}

	return formatDistanceToNow(date, { addSuffix: true, locale: fr });
}

export function createdAtFormatterRaw(
	createdAt: string | number | Date | { seconds: number; nanoseconds: number },
): string {
	// Handle falsy inputs (null, undefined, "", 0).
	// Note: If `0` is a valid timestamp (epoch start) that needs formatting,
	// this check should be `if (createdAt == null)`.
	// Current behavior matches the original: `!0` is true, so 0 returns "".
	if (!createdAt) {
		return "";
	}

	let dateToFormat: Date;

	// Check if createdAt is the specific Firebase-like timestamp object.
	// It must be a non-null object and have 'seconds' and 'nanoseconds' properties of type number.
	if (
		typeof createdAt === "object" &&
		createdAt !== null && // Essential: typeof null is 'object'
		"seconds" in createdAt &&
		typeof createdAt.seconds === "number" &&
		"nanoseconds" in createdAt && // As per the input type definition
		typeof createdAt.nanoseconds === "number" // As per the input type definition
	) {
		// It's the { seconds, nanoseconds } object
		const timestamp = createdAt as { seconds: number; nanoseconds: number };
		const milliseconds =
			timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000);
		dateToFormat = new Date(milliseconds);
	} else {
		// It's a string, number, or Date object.
		// new Date() handles these types:
		// - If createdAt is a Date instance, a new Date object with the same time value is created.
		// - If createdAt is a string, it attempts to parse it.
		// - If createdAt is a number, it's treated as milliseconds since epoch.
		// The cast (string | number | Date) is safe here because the `if` condition
		// has already filtered out the {seconds, nanoseconds} object type from the union.
		dateToFormat = new Date(createdAt as string | number | Date);
	}

	// Validate if the date conversion was successful.
	if (isNaN(dateToFormat.getTime())) {
		// This can happen if `createdAt` was an unparseable string,
		// or an object that `new Date()` couldn't process (e.g., `new Date({})`).
		return ""; // Return empty string for invalid dates, consistent with original.
	}

	// Format the date to 'dd/MM/YYYY HH:mm:ss'.
	// 'dd': day of the month (e.g., 01, 15, 31)
	// 'MM': month (e.g., 01 for January, 12 for December)
	// 'yyyy': year (e.g., 2023)
	// 'HH': hour in 24-hour format (00-23)
	// 'mm': minute (00-59)
	// 'ss': second (00-59)
	return format(dateToFormat, "dd/MM/yyyy HH:mm");
}
